import { useContext } from 'react';
import {
  compareDateString,
  getMonth,
  getYear,
} from '../../../../../../Utils/dateUtils';
import {
  isDateinLockedBokfPeriod,
  isDateinLockedBokfYear,
} from '../../../../Ekonomi/ObjektVyer/Bokforder/Components/BokforderPeriodDialog/Utils/BokforderPeriodDialogUtils';
import {
  IFieldValidation,
  IValidateFn,
} from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import KonteringContext from '../../../../_Common/Components/KonteringManager/KonteringContext';
import { FakturaDelQuery_node_TEDLRFakturaDel } from '../Queries/_QueryTypes/FakturaDelQuery';
import { validatePeriodiseringRedovisningstyp } from '../Utils/FakturadelValidationUtils';

export const useValidatePeriodDates: () => IValidateFn<FakturaDelQuery_node_TEDLRFakturaDel> =
  () => {
    const konteringContext = useContext(KonteringContext);
    return ({ currentValues, touched }) => {
      //   const errorMessages: IFieldValidation[] = [];
      const periodKonteringsforslag = (
        currentValues._Associations?.Faktura?._Associations
          ?.Konteringsforslag_All ?? []
      )?.filter(kf => kf.PERIODISERAD);

      const kodplanId =
        currentValues._Associations?.Faktura?._Associations?.Registrering
          ?._Associations?.Bokfverifikationer?._Associations?.Kodplan?.id ?? '';

      const bokfDate = new Date(
        currentValues._Associations?.Faktura?._Associations?.Registrering?._Associations?.Bokfverifikationer?.DATUM
      );

      const errors: IFieldValidation[] = periodKonteringsforslag.reduce(
        (prev, curr) => {
          const errorMessages: IFieldValidation[] = [];

          const fromDateTextValue = curr.PER_FROM_DATUM;
          const toDateTextValue = curr.PER_TOM_DATUM;

          const baskonto =
            konteringContext.konteringUtils.getKontodelByKontodelID(
              kodplanId,
              1,
              curr._Associations?.Kontodel1?.id ?? '',
              bokfDate
            );

          const redovisningstypError = validatePeriodiseringRedovisningstyp(
            baskonto?.redovisningsTyp,
            curr.id
          );
          if (redovisningstypError) {
            errorMessages.push(redovisningstypError);
          }

          if (fromDateTextValue == null || fromDateTextValue?.trim() === '') {
            const error: IFieldValidation = {
              fieldName: 'PER_FROM_DATUM',
              message: 'Fr o m datum saknar värde',
              path: ['Faktura', 'Konteringsforslag', curr.id],
              showAsHint:
                !touched?.Faktura?.Konteringsforslag?.[curr.id]?.PER_FROM_DATUM,
              type: 'MissingValue',
            };
            errorMessages.push(error);
          }

          if (toDateTextValue == null || toDateTextValue?.trim() === '') {
            const error: IFieldValidation = {
              fieldName: 'PER_TOM_DATUM',
              message: 'T o m datum saknar värde',
              path: ['Faktura', 'Konteringsforslag', curr.id],
              showAsHint:
                !touched?.Faktura?.Konteringsforslag?.[curr.id]?.PER_TOM_DATUM,
              type: 'MissingValue',
            };
            errorMessages.push(error);
          }
          const fromDate = new Date(fromDateTextValue);
          const toDate = new Date(toDateTextValue);

          const validationBokfDate = new Date(bokfDate.setDate(1));

          if (
            toDate.toString() === 'Invalid date' &&
            toDateTextValue != null &&
            toDateTextValue?.trim() != ''
          ) {
            const error: IFieldValidation = {
              fieldName: 'PER_TOM_DATUM',
              message: 'T o m datumet är felaktigt',
              path: ['Faktura', 'Konteringsforslag', curr.id],
              type: 'Error',
            };
            errorMessages.push(error);
          }
          if (
            fromDate.toString() === 'Invalid date' &&
            fromDateTextValue != null &&
            fromDateTextValue?.trim() != ''
          ) {
            const error: IFieldValidation = {
              fieldName: 'PER_FROM_DATUM',
              message: 'Fr o m datumet är felaktigt',
              path: ['Faktura', 'Konteringsforslag', curr.id],
              type: 'Error',
            };
            errorMessages.push(error);
          }
          if (errorMessages.length > 0) {
            return [...prev, ...errorMessages];
          }

          if (fromDate > toDate) {
            const errorTodate: IFieldValidation = {
              fieldName: 'PER_TOM_DATUM',
              message: 'T o m datumet är tidigare än fr o m datumet',
              path: ['Faktura', 'Konteringsforslag', curr.id],
              type: 'Error',
            };

            const errorFromdate: IFieldValidation = {
              fieldName: 'PER_FROM_DATUM',
              message: 'Fr o m datumet är senare än t o m datumet',
              path: ['Faktura', 'Konteringsforslag', curr.id],
              type: 'Error',
            };

            errorMessages.push(errorTodate);
            errorMessages.push(errorFromdate);
          }

          //Validering som först körs när man har matat in datumen i rätt format

          const fromDateSetToFirstDay = new Date(fromDate.setDate(1));
          const toDateSetToFirstDay = new Date(toDate.setDate(1));

          const toYear = getYear(toDateSetToFirstDay);
          const bokfMonth = getMonth(validationBokfDate);
          const toMonth = getMonth(toDateSetToFirstDay);
          const fromYear = getYear(fromDateSetToFirstDay);
          const bokfYear = getYear(validationBokfDate);
          const fromMonth = getMonth(fromDateSetToFirstDay);
          const diffBokfYearFromYear = bokfYear - fromYear;
          const diffBokfYearToYear = bokfYear - toYear;

          // Framåt i tiden
          if (toDateSetToFirstDay > validationBokfDate) {
            const bokfDatePlusFiveYears = new Date(
              getYear(validationBokfDate) + 5,
              getMonth(validationBokfDate),
              1
            );
            if (toDateSetToFirstDay > bokfDatePlusFiveYears) {
              const error: IFieldValidation = {
                fieldName: 'PER_TOM_DATUM',
                message:
                  'Det är inte tillåtet att periodisera över mer än 60 månader',
                path: ['Faktura', 'Konteringsforslag', curr.id],
                type: 'Error',
              };
              errorMessages.push(error);
            }
          }

          if (
            compareDateString(toDateSetToFirstDay, validationBokfDate) === 0
          ) {
            const error: IFieldValidation = {
              fieldName: 'PER_TOM_DATUM',
              message:
                'Det är inte tillåtet att periodisera på samma period som bokföringsdatumet',
              path: ['Faktura', 'Konteringsforslag', curr.id],
              type: 'Error',
            };
            errorMessages.push(error);
          }

          // Bakåt i tiden
          if (
            toDateSetToFirstDay <= validationBokfDate &&
            fromDateSetToFirstDay < validationBokfDate
          ) {
            // Validation på från-period som är beroende på till-Period
            if (
              diffBokfYearFromYear != 1 &&
              (diffBokfYearFromYear != 0 || diffBokfYearToYear != 0)
            ) {
              if (diffBokfYearFromYear != 0) {
                const error: IFieldValidation = {
                  fieldName: 'PER_FROM_DATUM',
                  message: 'Det går bara att periodisera till föregående år',
                  path: ['Faktura', 'Konteringsforslag', curr.id],
                  type: 'Error',
                };
                errorMessages.push(error);
              }
            }

            //Samma år som bokfDatum
            if (diffBokfYearToYear === 0 && bokfMonth > 3) {
              if (bokfMonth - fromMonth != 1) {
                const error: IFieldValidation = {
                  fieldName: 'PER_FROM_DATUM',
                  message: 'Man kan bara periodisera en månad bakåt under året',
                  path: ['Faktura', 'Konteringsforslag', curr.id],
                  type: 'Error',
                };
                errorMessages.push(error);
              }
            }
            // Kan bara periodiesera till december förgående år
            if (bokfMonth <= 3) {
              if (diffBokfYearFromYear === 1 && fromMonth != 12) {
                const error: IFieldValidation = {
                  fieldName: 'PER_FROM_DATUM',
                  message:
                    'Periodisering till föregående år kan endast göras till period december',
                  path: ['Faktura', 'Konteringsforslag', curr.id],
                  type: 'Error',
                };
                errorMessages.push(error);
              }
            }

            if (diffBokfYearToYear === 1 && bokfMonth > 3) {
              const error: IFieldValidation = {
                fieldName: 'PER_TOM_DATUM',
                message:
                  'Periodisering till föregående år kan endast göras under period jan-mars',
                path: ['Faktura', 'Konteringsforslag', curr.id],
                type: 'Error',
              };
              errorMessages.push(error);
            }

            if (
              diffBokfYearFromYear != 1 &&
              (diffBokfYearFromYear != 0 || diffBokfYearToYear != 0)
            ) {
              if (diffBokfYearToYear != 0) {
                const error: IFieldValidation = {
                  fieldName: 'PER_TOM_DATUM',
                  message: 'Det går bara att periodisera till föregående år',
                  path: ['Faktura', 'Konteringsforslag', curr.id],
                  type: 'Error',
                };
                errorMessages.push(error);
              }
            }

            //Samma år som bokfDatum
            if (diffBokfYearToYear === 0 && bokfMonth > 3) {
              if (bokfMonth - toMonth > 1) {
                const error: IFieldValidation = {
                  fieldName: 'PER_TOM_DATUM',
                  message: 'Man kan bara periodisera en månad bakåt under året',
                  path: ['Faktura', 'Konteringsforslag', curr.id],
                  type: 'Error',
                };
                errorMessages.push(error);
              }
            }

            // Kan bara periodiesera till december förgående år
            if (bokfMonth <= 3) {
              if (diffBokfYearToYear === 1 && toMonth != 12) {
                const error: IFieldValidation = {
                  fieldName: 'PER_TOM_DATUM',
                  message:
                    'Periodisering till föregående år kan endast göras till period december',
                  path: ['Faktura', 'Konteringsforslag', curr.id],
                  type: 'Error',
                };
                errorMessages.push(error);
              }
            }
          }

          // Låst år
          if (
            isDateinLockedBokfYear(
              toDateSetToFirstDay,
              konteringContext.bokforingsAr
            ) &&
            fromYear < bokfYear &&
            toYear < bokfYear
          ) {
            const error: IFieldValidation = {
              fieldName: 'PER_TOM_DATUM',
              message: 'T o m datumet är i ett låst bokföringsår',
              path: ['Faktura', 'Konteringsforslag', curr.id],
              type: 'Error',
            };
            errorMessages.push(error);
          } else {
            // Låst period

            if (
              isDateinLockedBokfPeriod(
                toDateSetToFirstDay,
                konteringContext.bokforingsAr
              ) &&
              fromDateSetToFirstDay < validationBokfDate &&
              toDateSetToFirstDay < validationBokfDate
            ) {
              const error: IFieldValidation = {
                fieldName: 'PER_TOM_DATUM',
                message: 'T o m datumet är i en låst bokföringsperiod',
                path: ['Faktura', 'Konteringsforslag', curr.id],
                type: 'Error',
              };
              errorMessages.push(error);
            }
          }

          // Låst år
          if (
            isDateinLockedBokfYear(
              fromDateSetToFirstDay,
              konteringContext.bokforingsAr
            ) &&
            fromYear < bokfYear &&
            toYear < bokfYear
          ) {
            const error: IFieldValidation = {
              fieldName: 'PER_FROM_DATUM',
              message: 'Fr o m datumet är i ett låst bokföringsår',
              path: ['Faktura', 'Konteringsforslag', curr.id],
              type: 'Error',
            };
            errorMessages.push(error);
          } else {
            if (
              isDateinLockedBokfPeriod(
                fromDateSetToFirstDay,
                konteringContext.bokforingsAr
              ) &&
              fromDateSetToFirstDay < validationBokfDate &&
              toDateSetToFirstDay < validationBokfDate
            ) {
              const error: IFieldValidation = {
                fieldName: 'PER_FROM_DATUM',
                message: 'Fr o m datumet är i en låst bokföringsperiod',
                path: ['Faktura', 'Konteringsforslag', curr.id],
                type: 'Error',
              };
              errorMessages.push(error);
            }
          }
          if (errorMessages.length > 0) {
            return [...prev, ...errorMessages];
          }

          return prev;
        },
        [] as IFieldValidation[]
      );

      return errors;
    };
  };
